import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type VisibilityLevel = "public" | "member" | "admin" | "hidden";
export type ModuleType = "page" | "section";

export type VisibilityEntry = {
  module_id: string;
  module_type: ModuleType;
  visibility: VisibilityLevel;
};

type VisibilityContextValue = {
  loading: boolean;
  entries: Record<string, VisibilityEntry>;
  getVisibility: (moduleId: string) => VisibilityLevel;
  setVisibility: (args: { moduleId: string; moduleType: ModuleType; visibility: VisibilityLevel }) => Promise<void>;
  counts: { hidden: number; member: number; admin: number };
  isPublicMode: boolean;
  activatePublicMode: () => Promise<void>;
  deactivatePublicMode: () => Promise<void>;
};

const VisibilityContext = createContext<VisibilityContextValue | undefined>(undefined);

export function VisibilityProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<Record<string, VisibilityEntry>>({});
  const [isPublicMode, setIsPublicMode] = useState(false);
  const { isAdmin, user } = useAuth();

  // Fetch visibility settings and public mode state
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Fetch visibility settings
      const { data: settingsData, error: settingsError } = await (supabase as any)
        .from("visibility_settings")
        .select("module_id, module_type, visibility");

      // Fetch public mode state
      const { data: modeData, error: modeError } = await (supabase as any)
        .from("visibility_mode")
        .select("is_public_mode")
        .eq("id", 1)
        .single();

      if (cancelled) return;

      if (settingsError) {
        setEntries({});
      } else {
        const map: Record<string, VisibilityEntry> = {};
        (settingsData ?? []).forEach((row: any) => {
          map[row.module_id] = {
            module_id: row.module_id,
            module_type: row.module_type,
            visibility: row.visibility,
          };
        });
        setEntries(map);
      }

      if (!modeError && modeData) {
        setIsPublicMode(modeData.is_public_mode ?? false);
      }

      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const getVisibility = useCallback((moduleId: string): VisibilityLevel => {
    // When public mode is active, everything is public
    if (isPublicMode) return "public";
    return entries[moduleId]?.visibility ?? "public";
  }, [entries, isPublicMode]);

  const setVisibility: VisibilityContextValue["setVisibility"] = async ({ moduleId, moduleType, visibility }) => {
    console.log("setVisibility called:", { moduleId, moduleType, visibility, isAdmin });
    
    if (!isAdmin) {
      console.warn("setVisibility: User is not admin, operation cancelled");
      return;
    }

    // optimistic update
    setEntries((prev) => ({
      ...prev,
      [moduleId]: { module_id: moduleId, module_type: moduleType, visibility },
    }));

    const { error } = await (supabase as any)
      .from("visibility_settings")
      .upsert(
        { module_id: moduleId, module_type: moduleType, visibility },
        { onConflict: "module_id" }
      );

    if (error) {
      console.error("Error updating visibility_settings:", error);
      // revert by refetching just once
      const { data } = await (supabase as any)
        .from("visibility_settings")
        .select("module_id, module_type, visibility")
        .eq("module_id", moduleId)
        .maybeSingle();

      setEntries((prev) => {
        const next = { ...prev };
        if (data) {
          next[moduleId] = data;
        } else {
          delete next[moduleId];
        }
        return next;
      });
    } else {
      console.log("Visibility updated successfully:", { moduleId, visibility });
    }
  };

  const activatePublicMode = useCallback(async () => {
    if (!isAdmin || !user) return;

    // Save current settings as a preset before activating
    const currentSettings = Object.values(entries);
    
    // Check if we already have a "before-public-mode" preset
    const { data: existingPreset } = await (supabase as any)
      .from("visibility_presets")
      .select("id")
      .eq("name", "before-public-mode")
      .maybeSingle();

    if (existingPreset) {
      // Update existing preset
      await (supabase as any)
        .from("visibility_presets")
        .update({ settings: currentSettings, created_at: new Date().toISOString() })
        .eq("id", existingPreset.id);
    } else {
      // Create new preset
      await (supabase as any)
        .from("visibility_presets")
        .insert({
          name: "before-public-mode",
          settings: currentSettings,
          created_by: user.id,
          is_active: true,
        });
    }

    // Activate public mode
    const { error } = await (supabase as any)
      .from("visibility_mode")
      .update({ 
        is_public_mode: true, 
        activated_at: new Date().toISOString(),
        activated_by: user.id 
      })
      .eq("id", 1);

    if (!error) {
      setIsPublicMode(true);
    }
  }, [isAdmin, user, entries]);

  const deactivatePublicMode = useCallback(async () => {
    if (!isAdmin) return;

    // Deactivate public mode first
    const { error } = await (supabase as any)
      .from("visibility_mode")
      .update({ 
        is_public_mode: false, 
        activated_at: null,
        activated_by: null 
      })
      .eq("id", 1);

    if (error) {
      console.error("Error deactivating public mode:", error);
      return;
    }

    setIsPublicMode(false);

    // Restore settings from preset
    const { data: preset } = await (supabase as any)
      .from("visibility_presets")
      .select("settings")
      .eq("name", "before-public-mode")
      .maybeSingle();

    if (preset?.settings && Array.isArray(preset.settings)) {
      // Restore each setting
      const restoredMap: Record<string, VisibilityEntry> = {};
      for (const entry of preset.settings) {
        restoredMap[entry.module_id] = entry;
      }
      setEntries(restoredMap);
    }
  }, [isAdmin]);

  const counts = useMemo(() => {
    const list = Object.values(entries);
    return {
      hidden: list.filter((e) => e.visibility === "hidden").length,
      member: list.filter((e) => e.visibility === "member").length,
      admin: list.filter((e) => e.visibility === "admin").length,
    };
  }, [entries]);

  const value = useMemo<VisibilityContextValue>(
    () => ({ 
      loading, 
      entries, 
      getVisibility, 
      setVisibility, 
      counts,
      isPublicMode,
      activatePublicMode,
      deactivatePublicMode,
    }),
    [loading, entries, counts, isAdmin, isPublicMode, getVisibility, activatePublicMode, deactivatePublicMode]
  );

  return <VisibilityContext.Provider value={value}>{children}</VisibilityContext.Provider>;
}

export function useVisibility() {
  const ctx = useContext(VisibilityContext);
  if (!ctx) throw new Error("useVisibility must be used within VisibilityProvider");
  return ctx;
}
